/* @flow */
import template from 'underscore/modules/template.js';
import adconfig from '../adconfig';
import adloader from '../adloader';
import _adserverbase from './adserverbase';
import bm from '../bidmanager-facade';
import containerHelper from '../container-helper';
import { logDebug, logError } from '../utils/logger';
import { extend, isNumber } from '../utils/coreutils';
import { isSecure, getHostname, getPageUrl } from '../utils/browserutil';
import { getRandom } from '../utils';

// We are going to use GAM style templating for interpolation. Let's use %%VAR%% here
const templateSettings: { [key: string]: unknown, ... } = {
  interpolate: /%%(.*?)%%/g
};

const HB_TEMPLATE = `<script type="text/JavaScript"> try{ window.top.__as.renderAd(window, document, "%%UOL_POS%%", "%%hb_bidder%%", "%%hb_adid%%"); } catch(e) {/*ignore*/} </script>`;

/**
 * Implements static ad server functionality
 *
 * @class
 * @exports adserver/staticas
 * @requires  module:utils
 * @requires  module:adconfig
 * @requires  module:adloader
 * @requires  module:adserverbase
 * @requires module:logger
 */

class _staticAS extends _adserverbase {
  _defConfig: staticASConfig;

  _config: staticASConfig;

  // flowlint-next-line unclear-type:off
  _staticMacros: Object;

  constructor(name: string) {
    super();
    this._name = name;
    // eslint-disable-next-line camelcase
    this._defConfig = extend(true, {}, this._defConfig, { min_hp_pb: 0 });
    this._config = extend(true, this._defConfig);
    this._staticMacros = {};
  }

  /**
   * Initialize the object - called from adserver initialize
   *
   * @param {window} w - window object
   * @param {Document} d - document object
   * @param {module:globalParams.globalParams} g - global param object
   * @returns {undefined}
   */
  init(w: WindowProxy, d: Document, g: globalParam) {
    super.init(w, d, g);
    this._staticMacros = {
      SITE: getHostname(),
      SCHEME: isSecure() ? 'https:' : 'http:'
    };
  }

  // flowlint-next-line unclear-type:off
  _processTempate(tmpl: string, obj: Object, posInfo: posConfigInfo): string {
    let out = tmpl;
    try {
      const fnTemplate = template(tmpl, templateSettings);
      const referrer = getPageUrl();

      // Build object with static & dynamic macros to expand
      const macros = extend(
        {},
        this._staticMacros,
        {
          REFERRER_URL: referrer,
          REFERRER_URL_UNESC: referrer,
          REFERRER_URL_ESC: escape(referrer),
          REFERRER_URL_ESC_ESC: escape(escape(referrer)),
          WIDTH: posInfo.width,
          HEIGHT: posInfo.height,
          CACHEBUSTER: getRandom()
        },
        obj
      );

      out = fnTemplate(macros);
    } catch (error) {
      logError(`${this._name} Exception in processing template ${error}`);
    }
    return out;
  }

  /**
   * Load ads without using ad server
   * render static ad for no bid/winning bid case
   * render partner ad if valid winning bid and bid greater than expected value
   *
   * @typedef {object} hbPosData
   * @param {Array<string>} posList - position list array
   * @param {object.<string, hbPosData>} o - header bidding return object
   * @param {object} customVars - custom variable to be populated inside iframe
   * @param {boolean} bRotation - For ad rotation?
   * @returns {undefined}
   */
  loadAds(
    posList: Array<string> = [],
    o: hbPartnerData = {},
    customVars: CustomVars = {},
    bRotation: boolean = false
  ) {
    posList.forEach(pos => {
      const as: posConfigInfo = adconfig.getPosSettings(pos);
      const posCVars = this._getCustomVars(pos, o, customVars, bRotation);
      const minHbPb = isNumber(as.min_hp_pb)
        ? Number(as.min_hp_pb)
        : Number(this._config.min_hp_pb);
      let isHB = false;
      if (posCVars && posCVars._QP) {
        const { hb_pb = '', hb_bidder = '' } = posCVars._QP;
        if (hb_bidder.length > 0 && hb_pb.length > 0) {
          const hbValue = bm.getDecodedBidFromBidder(hb_bidder, hb_pb);
          isHB = hbValue > minHbPb;
        }
      }

      containerHelper.createContainers(pos);
      // Get inactive container div of the ad position
      const adContainerFrameIds: adInfo = containerHelper.getAdContainerInfo(
        pos,
        false
      );

      let content = isHB ? HB_TEMPLATE : adconfig.getCreative(pos);

      if (!content.length) {
        logError(`${this._name} creative is missing for ${pos}`);
        return;
      }

      // query params are replaced in process template
      const qpVars = this._getQueryParams(pos, o, bRotation);
      content = this._processTempate(content, qpVars, as);

      // static ad - render content from config using adloader load function
      logDebug(
        `${this._name} queueing up ${pos} for rendering isHB:${isHB ? 1 : 0}`
      );
      adloader.load(
        pos,
        content,
        posCVars,
        this._name,
        false,
        adContainerFrameIds.cID
      );
    });
    adloader.checkQueue();
  }
}
const staticAS: _staticAS = new _staticAS('staticAS');
export default staticAS;
